import { TRACK_INFO, TRACK_PICKER_PROMPT } from '@aera/content/client';
import type { Lang, Track } from '@aera/schema';
import { tidy } from '@aera/ui';
import { ArrowRight, Clock, Globe2, Lock, MessageSquareText, Sparkles } from 'lucide-react';
import { Component, lazy, Suspense, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AeraMark, Button, LangToggle } from '../components/ui.js';

// Three.js loads only on this screen and the generating screen, never with the question screens.
const NeuralField = lazy(() => import('@aera/ui/three').then((m) => ({ default: m.NeuralField })));
const GlobeField = lazy(() => import('@aera/ui/three').then((m) => ({ default: m.GlobeField })));

/** If WebGL or the chunk fails (some WeChat webviews), the CSS glow behind stays and nothing else changes. */
export class SceneBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? null : this.props.children;
  }
}

const SCENE_CLASS = 'scene-fade-welcome absolute inset-x-0 top-0 h-[42svh] opacity-50 sm:opacity-70 sm:inset-y-0 sm:left-auto sm:right-0 sm:h-full sm:w-[60%]';

export function Welcome({
  lang,
  onLang,
  track,
  onPickTrack,
  onStart,
  resume,
  guided,
  busy,
}: {
  lang: Lang;
  onLang: (lang: Lang) => void;
  track: Track | null;
  onPickTrack: (track: Track) => void;
  onStart: () => void;
  resume: boolean;
  guided: boolean;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const lines = [
    { icon: MessageSquareText, text: t('welcome.line1') },
    { icon: Clock, text: t('welcome.line2') },
    { icon: Lock, text: t('welcome.line3') },
  ];
  return (
    <div className="relative flex min-h-[100svh] flex-col overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div
          className="absolute -right-24 top-16 h-[420px] w-[420px] rounded-full opacity-25 blur-3xl"
          style={{ background: 'radial-gradient(circle, var(--violet), var(--indigo) 45%, transparent 70%)' }}
        />
        <SceneBoundary>
          <Suspense fallback={null}>
            {track === 'overseas' ? (
              <GlobeField className={SCENE_CLASS} />
            ) : (
              <NeuralField calm className={SCENE_CLASS} />
            )}
          </Suspense>
        </SceneBoundary>
      </div>

      {/* safe-x-4, not px-4: these two span the width, so in landscape they must clear the notch. */}
      <header className="relative z-10 mx-auto flex w-full max-w-[640px] items-center justify-between safe-x-4 py-4 sm:max-w-5xl">
        <div className="flex items-center gap-2">
          <AeraMark />
          <span className="text-[15px] font-semibold tracking-tight">Aera</span>
        </div>
        <LangToggle lang={lang} onChange={onLang} />
      </header>

      <main className="relative z-10 mx-auto flex w-full max-w-[640px] flex-1 flex-col justify-end safe-x-4 pb-10 pt-[30svh] sm:max-w-5xl sm:justify-center sm:pt-10">
        <div className="max-w-[520px]">
          {guided && (
            <p className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-surface px-3 py-1 text-[13px] text-ink-2">
              <Sparkles className="h-3.5 w-3.5 text-indigo" aria-hidden />
              {t('welcome.guided')}
            </p>
          )}
          {track && <p className="lp-rise mb-3 text-[14px] font-medium text-indigo">{TRACK_INFO[track].name[lang]}</p>}
          <h1 className="lp-display lp-rise text-[clamp(2rem,7vw,3rem)]">{tidy(track ? TRACK_INFO[track].welcomeTitle[lang] : t('welcome.title'))}</h1>
          <ul className="mt-6 space-y-3">
            {lines.map(({ icon: Icon, text }, i) => (
              <li key={i} className="lp-rise flex gap-3 text-[16px] leading-relaxed text-ink-2" style={{ animationDelay: `${120 + i * 90}ms` }}>
                <Icon className="mt-1 h-4 w-4 shrink-0 text-indigo" aria-hidden />
                <span>{tidy(text)}</span>
              </li>
            ))}
          </ul>

          {track === null ? (
            <div className="lp-rise mt-8" style={{ animationDelay: '420ms' }}>
              <p className="mb-3 text-[15px] font-medium">{TRACK_PICKER_PROMPT[lang]}</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {(
                  [
                    { id: 'consulting', icon: Sparkles },
                    { id: 'overseas', icon: Globe2 },
                  ] as const
                ).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    disabled={busy}
                    onClick={() => onPickTrack(c.id)}
                    className="option-card lp-glass flex items-start gap-3 rounded-2xl border border-hair px-4 py-4 text-left hover:border-indigo disabled:opacity-60"
                  >
                    <c.icon className="mt-0.5 h-5 w-5 shrink-0 text-indigo" aria-hidden />
                    <span>
                      <span className="block text-[16px] font-semibold">{TRACK_INFO[c.id].name[lang]}</span>
                      <span className="mt-0.5 block text-[14px] leading-snug text-ink-2">{tidy(TRACK_INFO[c.id].pitch[lang])}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <Button variant="gradient" size="lg" onClick={onStart} busy={busy} className="lp-rise mt-8" style={{ animationDelay: '420ms' }}>
              {resume ? t('welcome.resume') : t('welcome.start')}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
